/**
 * SerialField — warehouse serial capture for a job-equipment unit. Two ways in:
 *   • type it by hand (the input, saves on blur), or
 *   • snap/upload a photo of the serial plate → `read-serial` (Gemini vision) reads it →
 *     a MANDATORY confirm step shows the photo + the read value, editable, and nothing
 *     saves until the crew taps Confirm (Seth 2026-06-29: "confirming serial correct after
 *     picture upload would be a must"). Works desktop (file) + mobile (camera). The photo is
 *     retained on the unit (serial_photo_path) so the office can always double-check.
 */
import { useEffect, useRef, useState } from "react";
import { Camera, Check, X, Loader2, RefreshCw, AlertTriangle, ArrowRightLeft } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSetEquipmentSerial, useMoveSerial, fetchSerialConflicts, serialErrorText, type JobEquipment, type SerialConflicts } from "./jobs";

const cnBtn = (half: boolean) => (half ? "inline-flex flex-1 items-center justify-center rounded-full px-4 py-2.5 text-sm font-semibold text-slate-500 transition-colors hover:text-slate-800" : "inline-flex w-full items-center justify-center rounded-full border border-hairline bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:border-slate-400");

const toB64 = (file: File) =>
  new Promise<string>((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result).split(",")[1] || "");
    r.onerror = rej;
    r.readAsDataURL(file);
  });

type Pending = { previewUrl: string; path: string; read: string; serial: string };
/** SERIAL-GUARD-01: a serial that already lives somewhere else, waiting on a decision. */
type Conflict = { serial: string; photoPath?: string | null; found: SerialConflicts };

export function SerialField({ jobId, equip, disabled }: { jobId: string; equip: JobEquipment; disabled?: boolean }) {
  const setSerial = useSetEquipmentSerial();
  const moveSerial = useMoveSerial();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [conflict, setConflict] = useState<Conflict | null>(null);
  const [checking, setChecking] = useState(false);

  // SERIAL-GUARD-01 (Seth: "a warning if a duplicate serial is entered across
  // Pegasus"): look before saving. Another OPEN job → the sheet offers to MOVE
  // the unit here (the database refuses the plain save anyway). A closed job
  // or an installed unit → a warning they can override.
  const save = async (serial: string, photoPath?: string | null) => {
    setErr(null);
    const v = serial.trim().toUpperCase();
    if (!v) { setSerial.mutate({ id: equip.id, serial: null, ...(photoPath !== undefined ? { serial_photo_path: photoPath } : {}) }); return; }
    setChecking(true);
    try {
      const found = await fetchSerialConflicts(v, equip.id);
      if (found.jobs.length || found.installed.length) { setConflict({ serial: v, photoPath, found }); return; }
    } catch { /* the database still guards the save */ } finally { setChecking(false); }
    setSerial.mutate({ id: equip.id, serial: v, ...(photoPath !== undefined ? { serial_photo_path: photoPath } : {}) }, { onError: (e) => setErr(serialErrorText(e)) });
  };
  const useAnyway = () => {
    if (!conflict) return;
    setSerial.mutate({ id: equip.id, serial: conflict.serial, ...(conflict.photoPath !== undefined ? { serial_photo_path: conflict.photoPath } : {}) }, {
      onSuccess: () => setConflict(null),
      onError: (e) => { setConflict(null); setErr(serialErrorText(e)); },
    });
  };
  const moveHere = (fromEquipId: string) => {
    moveSerial.mutate({ fromEquipId, toEquipId: equip.id }, {
      onSuccess: () => setConflict(null),
      onError: (e) => { setConflict(null); setErr((e as Error)?.message || "Couldn't move it — try again."); },
    });
  };

  // Esc closes the confirm modal (outside-click + X also close it — the 3-affordance rule).
  useEffect(() => {
    if (!pending) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") closePending(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  const closePending = () => {
    if (pending) URL.revokeObjectURL(pending.previewUrl);
    setPending(null);
  };

  const onPick = async (file: File) => {
    setErr(null);
    setBusy(true);
    try {
      const b64 = await toB64(file);
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase().replace(/[^a-z0-9]/g, "") || "jpg";
      const path = `job/${jobId}/${equip.id}-${Date.now()}.${ext}`;
      const up = await supabase.storage.from("job-media").upload(path, file, {
        upsert: true,
        contentType: file.type || "image/jpeg",
      });
      if (up.error) throw up.error;
      const { data, error } = await supabase.functions.invoke("read-serial", {
        body: { image_base64: b64, mime: file.type || "image/jpeg" },
      });
      if (error) throw error;
      const read = String(data?.serial || "").toUpperCase();
      setPending({ previewUrl: URL.createObjectURL(file), path, read, serial: read });
    } catch (e) {
      setErr((e as Error)?.message || "Couldn't read that photo — enter the serial by hand.");
    } finally {
      setBusy(false);
    }
  };

  const confirm = () => {
    if (!pending) return;
    const v = pending.serial.trim().toUpperCase();
    const path = pending.path;
    closePending();
    void save(v, path);
  };

  return (
    <>
      <div className="flex items-center gap-1.5">
        <input
          defaultValue={equip.serial ?? ""}
          disabled={disabled}
          placeholder="serial #"
          onBlur={(ev) => {
            const v = ev.target.value.trim().toUpperCase();
            if ((v || null) !== (equip.serial ?? null)) void save(v);
          }}
          className="jinp w-36 uppercase placeholder:normal-case"
        />
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); e.currentTarget.value = ""; }}
        />
        <button
          type="button"
          disabled={disabled || busy}
          onClick={() => fileRef.current?.click()}
          className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:border-brand-400 hover:text-brand-700 disabled:opacity-50"
          title="Photograph the serial plate"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
          {busy ? "Reading…" : "Scan"}
        </button>
        {equip.serial_photo_path && !pending && <span className="text-[10px] font-medium text-emerald-600">photo ✓</span>}
        {checking && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" aria-label="Checking this serial" />}
      </div>
      {err && <div className="mt-1 text-[11px] text-rose-500">{err}</div>}

      {conflict && (() => {
        const openJobs = conflict.found.jobs.filter((j) => j.open);
        const closedJobs = conflict.found.jobs.filter((j) => !j.open);
        const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : null);
        const who = (email: string | null) => (email ? email.split("@")[0] : null);
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setConflict(null)}>
            <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="mb-3 flex items-start justify-between gap-3">
                <div className="flex items-start gap-2.5">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-amber-100 text-amber-700"><AlertTriangle className="h-5 w-5" /></span>
                  <div>
                    <h3 className="text-base font-bold text-slate-900">This serial is already in Pegasus</h3>
                    <div className="mt-0.5 font-mono text-sm font-semibold text-slate-700">{conflict.serial}</div>
                  </div>
                </div>
                <button onClick={() => setConflict(null)} className="text-slate-400 transition-colors hover:text-slate-700" aria-label="Close"><X className="h-5 w-5" /></button>
              </div>

              {openJobs.length > 0 && (
                <div className="space-y-2">
                  {openJobs.map((j) => (
                    <div key={j.equip_id} className="rounded-xl border border-amber-200 bg-amber-50/70 px-3.5 py-3">
                      <div className="text-[10.5px] font-extrabold uppercase tracking-wide text-amber-700">Pulled for another open job</div>
                      <div className="mt-0.5 text-sm font-bold text-slate-900">{j.job_title ?? j.dispatch ?? "Job"}{j.customer ? <span className="font-medium text-slate-500"> · {j.customer}</span> : null}</div>
                      <div className="text-[12px] text-slate-600">
                        {[j.manufacturer, j.model].filter(Boolean).join(" ")}
                        {j.pulled_by ? ` · pulled by ${who(j.pulled_by)}${when(j.pulled_at) ? ` ${when(j.pulled_at)}` : ""}` : ""}
                        {j.install_date ? ` · installs ${when(j.install_date)}` : ""}
                      </div>
                      <button
                        type="button"
                        disabled={moveSerial.isPending}
                        onClick={() => moveHere(j.equip_id)}
                        className="mt-2.5 inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-slate-900 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-slate-800 disabled:opacity-50"
                      >
                        <ArrowRightLeft className="h-4 w-4" /> {moveSerial.isPending ? "Moving…" : "Move it to this job"}
                      </button>
                      <p className="mt-1.5 text-[11px] leading-snug text-slate-500">That job goes back to needs-a-pull for this unit, and both jobs get a note saying who moved it.</p>
                    </div>
                  ))}
                </div>
              )}

              {(closedJobs.length > 0 || conflict.found.installed.length > 0) && (
                <div className={openJobs.length ? "mt-3" : ""}>
                  {conflict.found.installed.map((u, i) => (
                    <div key={`i${i}`} className="rounded-xl border border-hairline bg-slate-50 px-3.5 py-3">
                      <div className="text-[10.5px] font-extrabold uppercase tracking-wide text-slate-500">Already installed{u.source === "esc" ? " (ESC record)" : ""}</div>
                      <div className="mt-0.5 text-sm font-bold text-slate-900">{u.customer ?? u.custno ?? "Customer"}</div>
                      <div className="text-[12px] text-slate-600">{[u.address, u.city].filter(Boolean).join(", ")}{[u.manufacturer, u.model].filter(Boolean).length ? ` · ${[u.manufacturer, u.model].filter(Boolean).join(" ")}` : ""}{u.install_date ? ` · installed ${when(u.install_date)}` : ""}</div>
                    </div>
                  ))}
                  {closedJobs.map((j) => (
                    <div key={j.equip_id} className="mt-2 rounded-xl border border-hairline bg-slate-50 px-3.5 py-3">
                      <div className="text-[10.5px] font-extrabold uppercase tracking-wide text-slate-500">On a closed job</div>
                      <div className="mt-0.5 text-sm font-bold text-slate-900">{j.job_title ?? j.dispatch ?? "Job"}{j.customer ? <span className="font-medium text-slate-500"> · {j.customer}</span> : null}</div>
                      <div className="text-[12px] text-slate-600">{[j.manufacturer, j.model].filter(Boolean).join(" ")}</div>
                    </div>
                  ))}
                  <p className="mt-2 text-[12px] leading-snug text-slate-600">Double-check the plate — a serial usually lives in one place. If this really is that unit (a return, a swap), you can use it anyway.</p>
                </div>
              )}

              <div className="mt-4 flex items-center gap-2">
                {openJobs.length === 0 && (
                  <button
                    type="button"
                    disabled={setSerial.isPending}
                    onClick={useAnyway}
                    className="inline-flex flex-1 items-center justify-center rounded-full border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-800 transition-colors hover:border-slate-500 disabled:opacity-50"
                  >
                    {setSerial.isPending ? "Saving…" : "Use it anyway"}
                  </button>
                )}
                <button type="button" onClick={() => setConflict(null)} className={cnBtn(openJobs.length === 0)}>Cancel</button>
              </div>
            </div>
          </div>
        );
      })()}

      {pending && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={closePending}>
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-base font-bold text-slate-900">Confirm the serial number</h3>
              <button onClick={closePending} className="text-slate-400 transition-colors hover:text-slate-700" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <img
              src={pending.previewUrl}
              alt="serial plate"
              className="mb-3 max-h-60 w-full rounded-lg border border-hairline bg-slate-50 object-contain"
            />
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
              Pegasus read this — check it against the photo
            </p>
            <input
              autoFocus
              value={pending.serial}
              onChange={(e) => setPending({ ...pending, serial: e.target.value.toUpperCase() })}
              className="w-full rounded-lg border-2 border-brand-300 bg-white px-3 py-2.5 text-lg font-bold uppercase tracking-wide text-slate-900 focus:border-brand-500 focus:outline-none"
            />
            {!pending.read && (
              <p className="mt-1 text-[11px] text-amber-600">Couldn’t read it automatically — type it from the photo.</p>
            )}
            <div className="mt-4 flex items-center gap-2">
              <button
                onClick={confirm}
                disabled={!pending.serial.trim() || setSerial.isPending}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
              >
                <Check className="h-4 w-4" /> {setSerial.isPending ? "Saving…" : "Confirm serial"}
              </button>
              <button
                onClick={() => { closePending(); setTimeout(() => fileRef.current?.click(), 50); }}
                className="inline-flex items-center gap-1.5 rounded-full border border-hairline bg-white px-3 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:border-brand-400"
              >
                <RefreshCw className="h-4 w-4" /> Retake
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
